import BorderedBox from "./BorderedBox";
import {connect} from "react-redux";
import {Box, Button, CircularProgress, IconButton, List, ListItemButton, ListItemText, TextField} from "@mui/material";
import React, {ChangeEventHandler, useContext, useEffect, useState} from "react";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {selectUser, selectVersions} from "../../redux/selectors/firebase";
import {selectUserId} from "../../redux/selectors/editor";
import {ErrorBoundary} from "@sentry/react";
import makeStyles from "@mui/styles/makeStyles";
import {AddCircle, Stop} from "@mui/icons-material";
import {FirebaseTools, IDatabaseFingerprint} from "../../utility/firebaseTools";
import VersionDetails from "./VersionDetails";
import {PathwayUser} from "../../redux/types/firebase";

const useStyles = makeStyles(theme => ({
    body: {
        display: "flex"
    },
    content: {
        display: "flex",
        flexDirection: "column",
        height: "100%"
    },
    details: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        margin: theme.spacing(1),
    },
    head: {
        display: "flex",
        height: 56
    },
    list: {
        display: "flex",
        flexDirection: "column",
        overflowY: "scroll",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    }
}));

type VersionTypes = {[key: string]: IVersion}

export interface IVersion {
    fingerprint: IDatabaseFingerprint
    tag: string
    timestamp: number
    user: string
    version: number
}
interface IVersions {
    project: string
    user: PathwayUser
    userId: string
    versions: VersionTypes
}
function Versions({project, user, userId, versions}: IVersions) {
    const classes = useStyles();
    const {push, track, untrack} = useContext(FirebaseContext);
    const [selectedVersionKey, setSelectedVersionKey] = useState<string>();
    const [previousVersion, setPreviousVersion] = useState<IVersion>();
    const [selectedVersion, setSelectedVersion] = useState<IVersion>();
    const [tag, setTag] = useState("");
    const [progress, setProgress] = useState<number>();
    const [versionNumber, setVersionNumber] = useState(0);
    const showCreateVersion = window.location.hostname.match(/(develop|localhost)/) !== null && user.roles.admin;
    const handleAddVersion = async () => {
        const fingerprint = await FirebaseTools.GetDatabaseFingerprint(project, (label, progress) =>
            setProgress(progress));

        setProgress(undefined);
        push(`versions/${project}/import`, {
            fingerprint,
            tag,
            timestamp: Date.now(),
            user: userId,
            version: versionNumber
        }).catch(console.error);
    };
    const handleSelectVersion = (key: string) => () => {
        setSelectedVersionKey(key);
        setSelectedVersion(versions[key]);
    }
    const handleTagChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setTag(target.value);
    const handleVersionChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setVersionNumber(Number(target.value));
    const addVersionDisabled = progress !== undefined ||
        Object.values(versions).find(version => version.version === versionNumber) !== undefined;
    const versionLabel = selectedVersion &&  
        `${new Date(selectedVersion.timestamp).toLocaleString()} : ${selectedVersion.tag}`;

    useEffect(() => {
        if (selectedVersion) {
            setPreviousVersion(Object.values(versions)
                .find(version => version.version === selectedVersion.version - 1));
        }
    }, [selectedVersion, versions]);
    useEffect(() => {
        const keys = Object.keys(versions);
        
        if (keys.length) {
            const lastKey = keys[keys.length - 1];

            setVersionNumber(versions[lastKey].version + 1);
        }
    }, [versions]);
    useEffect(() => {
        if (selectedVersionKey && versions[selectedVersionKey] === undefined) {
            setSelectedVersionKey(undefined);
            setSelectedVersion(undefined);
        }
    }, [selectedVersionKey, versions]);
    useEffect(() => {
        track(`versions/${project}/import`, ["versions"]);
        track("users", ["users"]);

        return () => {
            untrack(`versions/${project}/import`);
            untrack("users");
        }
    }, [project, track, untrack]);
    return (
        <BorderedBox label="Versions">
            <Box className={classes.content}>
                {showCreateVersion &&
                    <Box className={classes.head}>
                        <TextField label="Tag" onChange={handleTagChange} size="small" sx={{flexGrow: 1, m:1}}/>
                        <TextField label="Version" onChange={handleVersionChange} size="small" sx={{flexGrow: 1, m:1}}
                                   type="number" value={versionNumber}/>
                        {progress ?
                            <Button>
                                <Stop/>
                                <CircularProgress color="inherit" size={24} variant="determinate" value={progress}
                                                  style={{position: "absolute", zIndex: 1}}/>
                            </Button>
                            :
                            <IconButton disabled={addVersionDisabled} onClick={handleAddVersion} size="small" sx={{m:1}}
                                        title={"Add New Version"}>
                                <AddCircle/>
                            </IconButton>}
                    </Box>}
                <Box className={classes.body} sx={{height: showCreateVersion ? "calc(100% - 56px)" : "100%"}}>
                    <Box className={classes.list}>
                        <ErrorBoundary>
                            <List>
                                {Object.keys(versions).map((key, index) => {
                                    const version: IVersion = versions[key];
        
                                    return (
                                        <ListItemButton key={key} onClick={handleSelectVersion(key)}
                                                        selected={selectedVersionKey === key} sx={{m:1}}>
                                            <ListItemText primary={`v${version.version} ${version.tag}`}
                                                          secondary={new Date(version?.timestamp).toLocaleString()}/>
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </ErrorBoundary>
                    </Box>
                    <Box className={classes.details}>
                        {selectedVersion &&
                            <VersionDetails label={versionLabel} previous={previousVersion} version={selectedVersion}/>}
                    </Box>
                </Box>
            </Box>
        </BorderedBox>
    );
}
const mapStateToProps = (store: never) => ({
    user: selectUser(store),
    userId: selectUserId(store),
    versions: selectVersions(store) || {}
});

export default connect(mapStateToProps, {
})(Versions);
